/**
 * src/utils/sessionState.ts -- WP-011 Story 2 (AC-004, AC-005)
 *
 * Pure sessionStorage utility for session expiry state preservation.
 * Keys:
 *   attic_return_path  -- pathname the user was on when the session expired
 *   attic_form_draft   -- JSON-serialized active form field values
 *
 * All functions silently swallow exceptions (sessionStorage unavailable
 * in some privacy modes) so callers never need to guard against throws.
 */

const RETURN_PATH_KEY = "attic_return_path";
const FORM_DRAFT_KEY = "attic_form_draft";

// ---------------------------------------------------------------------------
// Return path helpers (AC-004)
// ---------------------------------------------------------------------------

/** Persist the current pathname so post-login navigation can return to it. */
export function saveReturnPath(pathname: string): void {
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, pathname);
  } catch {
    // Silently swallow: sessionStorage may be unavailable in private mode.
  }
}

/** Read the stored return path, or null if not set. */
export function getReturnPath(): string | null {
  try {
    return sessionStorage.getItem(RETURN_PATH_KEY);
  } catch {
    return null;
  }
}

/** Remove the return path key after it has been consumed by the login flow. */
export function clearReturnPath(): void {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // Silently swallow.
  }
}

// ---------------------------------------------------------------------------
// Form draft helpers (AC-005)
// ---------------------------------------------------------------------------

/**
 * Persist the active form field values.
 * When values is null or undefined the write is skipped (AC-005: attic_form_draft
 * is NOT written to sessionStorage when no form is active).
 */
export function saveFormDraft(values: Record<string, unknown> | null | undefined): void {
  if (values == null) return;
  try {
    sessionStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(values));
  } catch {
    // Silently swallow.
  }
}

/**
 * Read the stored form draft.
 * Returns null when absent, non-parseable, or not a plain object.
 */
export function getFormDraft(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(FORM_DRAFT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Remove the form draft key after it has been consumed by the target form. */
export function clearFormDraft(): void {
  try {
    sessionStorage.removeItem(FORM_DRAFT_KEY);
  } catch {
    // Silently swallow.
  }
}
